import type { RaceContestantComplete } from '@/objects/generated';
import { ValidatorDef } from '@/objects/gui';
import { type IAlertMessage } from '@/objects/IAlertMessage';
import { useEventCommonStore } from '@/stores';
import { findAvailableCategoriesForGenderAndBirthdayInCurrentEvent } from '@/utilities/categoryutils';
import { ref, watch, type Ref } from 'vue';

export type ValidationResult = 'valid' | 'invalid' | 'warning';

/**
 * Helper class that contains validation logic
 */
export class Validator extends ValidatorDef {
    public Message = ref<IAlertMessage | null>(null);

    constructor(value: ValidatorDef) {
        super(value);
    }

    /**
     * Returns true if the value is required and the value is not empty
     * @param value
     * @returns
     */
    private validateRequired(value: string | number | null): boolean {
        // Check if we can parse value as a number
        if (value?.toString()?.trim()?.length === 0) {
            return !this.Required;
        }

        if (!isNaN(value as any)) {
            return !this.Required || (value !== null && value !== undefined);
        } else {
            return !this.Required || (value ?? '').toString()?.trim()?.length > 0;
        }
    }

    /**
     * Will return true if the value is greater than or equal to the Min value or this.Min is null
     * @param value
     * @returns
     */
    private validateMin(value: number): boolean {
        return this.Min === null || value >= this.Min;
    }

    /**
     * Will return true if the value is less than or equal to the Max value or this.Max is null
     * @param value
     * @returns
     */
    private validateMax(value: number): boolean {
        return this.Max === null || value <= this.Max;
    }

    private validateEmail(value: string): boolean {
        if (this.Type !== 'email') {
            return true;
        }
        return value == '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    /**
     * Validate a string value
     * @param value
     * @returns
     */
    private validateString(value: string): boolean {
        // if one of the tests fails, return false
        if (!this.validateRequired(value)) {
            this.Message.value = { message: 'fieldvalidate.required', type: 'danger' };
            return false;
        }

        // Validate email with regex
        if (!this.validateEmail(value)) {
            this.Message.value = { message: 'fieldvalidate.email', type: 'danger' };
            return false;
        }

        return true;
    }

    /**
     * Validate a number value
     * @param value
     * @returns
     */
    private validateNumber(value: number): boolean {
        if (!this.validateRequired(value)) {
            this.Message.value = { message: 'fieldvalidate.required', type: 'danger' };
            return false;
        }

        if (!this.validateMin(value)) {
            this.Message.value = { message: 'fieldvalidate.minvalue', type: 'danger' };
            return false;
        }

        if (!this.validateMax(value)) {
            this.Message.value = { message: 'fieldvalidate.maxvalue', type: 'danger' };
            return false;
        }

        return true;
    }

    /**
     * This should be moved to its own validator and the ValidatorDef should be a base abstract class where we can override the Validate method but this has to be completely rewritten so this is left for later
     * @param arg
     * @param data
     * @returns
     */
    private validateContestantCategory(data?: RaceContestantComplete): ValidationResult {
        if (data == null || !data.CategoryID) return 'valid';

        return useEventCommonStore().IsValidCategoryCombination(
            data?.CategoryID ?? 0,
            data!.Gender ?? 'n',
            data.EventAge ?? data.Age ?? 0,
            data?.RelayTeamName,
        )
            ? 'valid'
            : 'warning';
    }

    /**
     * Validate a string or number value
     * @param value
     * @param data - additional data that can be used for validation usually the row object for the list
     * @returns
     */
    public Validate(arg: number | string | null | undefined, data?: any): ValidationResult {
        // if it is not required and the value is null or undefined or empty, return 'valid'
        if (!this.Required && (arg === null || arg === undefined)) {
            return 'valid';
        }

        if (this.Type == 'contestant') {
            return this.validateContestantCategory(data);
        }

        if (typeof arg === 'string' && (isNaN(Number(arg)) || arg.trim().length === 0)) {
            return this.validateString(arg) ? 'valid' : 'invalid';
        } else if (typeof arg === 'string' && !isNaN(Number(arg))) {
            return this.validateNumber(Number(arg)) ? 'valid' : 'invalid';
        } else {
            return this.validateNumber((arg ?? 0) as number) ? 'valid' : 'invalid';
        }
    }
}

/**
 * The exported validator composable
 * @param validator
 * @returns
 */
export const useValidator = (
    validator: ValidatorDef | null,
    modelValue: any,
): { Validate: (value: string | number) => boolean; ValidateMessage: Ref<IAlertMessage | null> } => {
    if (validator === null) {
        return { Validate: (value: string | number) => true, ValidateMessage: ref<IAlertMessage | null>(null) };
    }
    const handler = new Validator(validator);

    /**
     * Validate a value
     * @param value
     * @returns
     */
    const Validate = (value: string | number): boolean => {
        handler.Message.value = null;
        return handler.Validate(value) == 'valid';
    };

    watch(
        () => modelValue,
        (nv) => Validate(nv == null ? '' : nv.toString()),
        { immediate: true },
    );

    return { Validate, ValidateMessage: handler.Message };
};
